import React, { useState, useRef, useCallback } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, FeatureGroup, useMap } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-polylinedecorator";
import { LocateIcon } from "lucide-react";
import LineModal from "./modals/LineModals";
import { useEffect } from "react";
import MatriceConflitti from "./Conflitti";
import { Col, Row, Alert } from "antd";
import FasiChart from "./FasiChart";

const LocateButton = () => {
  const map = useMap();
  const locationMarker = useRef(null);
  const [isLocationVisible, setIsLocationVisible] = useState(false);

  const customIcon = L.icon({
    iconUrl: require("../assets/location.png"),
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });

  const toggleLocation = () => {
    if (isLocationVisible) {
      if (locationMarker.current) {
        map.removeLayer(locationMarker.current);
        locationMarker.current = null;
      }
      setIsLocationVisible(false);
    } else {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            map.setView([latitude, longitude], 17);

            if (locationMarker.current) {
              map.removeLayer(locationMarker.current);
            }

            locationMarker.current = L.marker([latitude, longitude], {
              icon: customIcon,
            })
              .addTo(map)
              .bindPopup("Sei qui")
              .openPopup();

            setIsLocationVisible(true);
          },
          (error) => {
            console.error("Error getting location:", error);
            <Alert
              message="Error"
              description="Unable to retrieve your location."
              type="error"
              showIcon
            />;
          }
        );
      } else {
        <Alert
          message="Warning"
          description="Geolocation is not supported by this browser."
          type="warning"
          showIcon
        />;
      }
    }
  };

  return (
    <button
      onClick={toggleLocation}
      style={{
        position: "absolute",
        bottom: 10,
        left: 10,
        zIndex: 1000,
        background: "white",
        border: "none",
        borderRadius: "50%",
        padding: "10px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0px 2px 5px rgba(6, 22, 247, 0.2)",
      }}
    >
      <LocateIcon size={24} />
    </button>
  );
};

const MapComponent = ({
  addFlusso,
  hoveredFlusso,
  flussi,
  setFlussi,
  resizeTrigger,
  conflittiSelezionati,
  setConflittiSelezionati,
  exposeUpdatePolylineColor,
  flussiModal,
  setFlussiModal,
  optimizedData,
}) => {
  const ZOOM_LEVEL = 19;
  const mapRef = useRef();
  const [center] = useState({
    lat: 45.640301546939185,
    lng: 9.611915782993783,
  });

  const deleteOnRef = useRef(false);
  const alreadySet = useRef(false);
  const featureGroupRef = useRef();
  const renderedPolylinesRef = useRef(new Set());
  const [currentColor, setCurrentColor] = useState("#000000");
  const [drawnItemsData, setDrawnItemsData] = useState([]);
  const [polylineDecorators, setPolylineDecorators] = useState(new Map());
  const [placingTrafficIcon, setPlacingTrafficIcon] = useState(false);
  const drawnItems = new L.FeatureGroup();
  const [polylineDecoratorsByLatlngs, setPolylineDecoratorsByLatlngs] =
    useState(new Map());
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("info");
  const [featureGroupKey, setFeatureGroupKey] = useState(Date.now());

  // --------------------------------------

  const showAlert = (message, type = "info") => {
    setAlertMessage(message);
    setAlertType(type);
    setAlertVisible(true);
    setTimeout(() => setAlertVisible(false), 5000);
  };

  const trafficIcon = L.icon({
    iconUrl: require("../assets/trafficLight.png"),
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
  const pedestrianIcon = L.icon({
    iconUrl: require("../assets/pedonale.png"),
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });

  const handleMapClick = (e) => {
    const { layerType, layer } = e;
    if (placingTrafficIcon) {
      const { lat, lng } = e.latlng;
      const marker = L.marker([lat, lng], {
        icon: trafficIcon,
        draggable: true,
      })
        .addTo(mapRef.current)
        .bindPopup("Traffic Icon")
        .openPopup();
      marker.on("dragend", (e) => {
        const { lat, lng } = e.target.getLatLng();
      });

      setPlacingTrafficIcon(false);
      showAlert("Traffic icon placed successfully", "success");
    } else if (layerType === "circle") {
      const { lat, lng } = layer.getLatLng();
      const marker = L.marker([lat, lng], {
        icon: pedestrianIcon,
        draggable: true,
      })
        .addTo(mapRef.current)
        .bindPopup("Pedestrian Icon")
        .openPopup();
      mapRef.current.removeLayer(layer);
      showAlert("Pedestrian icon placed successfully", "success");
    }
  };
  const toggleTrafficPlacement = () => {
    setPlacingTrafficIcon((prev) => !prev);
    showAlert(
      placingTrafficIcon
        ? "Traffic icon placement canceled"
        : "Click on map to place traffic icon",
      "info"
    );
  };
  const addPolylineDecorator = (layer, color) => {
    if (mapRef.current) {
      const polylineDecorator = L.polylineDecorator(layer, {
        patterns: [
          {
            offset: "1000%",
            repeat: 0,
            symbol: L.Symbol.arrowHead({
              pixelSize: 600,
              polygon: true,
              pathOptions: {
                stroke: true,
                color: color,
                weight: 30,
                fill: true,
                fillColor: color,
                fillOpacity: 10,
              },
            }),
          },
        ],
      });
      polylineDecorator.addTo(mapRef.current);
      showAlert("Polyline decorator added", "success");
    }
  };

  const deleteSingleLine = useCallback((layerOrLatlngs) => {
    if (!mapRef.current) return;

    const latlngsKey =
      layerOrLatlngs instanceof L.Polyline
        ? JSON.stringify(layerOrLatlngs.getLatLngs())
        : layerOrLatlngs;

    // Remove all related layers
    mapRef.current.eachLayer((layer) => {
      // Check for main polyline
      const isTargetPolyline =
        layer instanceof L.Polyline &&
        JSON.stringify(layer.getLatLngs()) === latlngsKey;

      // Check for decorators
      const isTargetDecorator =
        layer instanceof L.PolylineDecorator &&
        (layer._latlngsKey === latlngsKey ||
          (layer._layers &&
            Object.values(layer._layers).some(
              (l) => l._parentLatlngs === latlngsKey
            )));

      // Check for arrowheads
      const isTargetArrowHead =
        layer._isArrowHead && layer._parentLatlngs === latlngsKey;

      if (isTargetPolyline || isTargetDecorator || isTargetArrowHead) {
        mapRef.current.removeLayer(layer);
      }
    });

    // Update references
    renderedPolylinesRef.current.delete(latlngsKey);
    setPolylineDecorators((prev) => {
      const newMap = new Map(prev);
      newMap.forEach((decorator, polyline) => {
        if (
          polyline instanceof L.Polyline &&
          JSON.stringify(polyline.getLatLngs()) === latlngsKey
        ) {
          newMap.delete(polyline);
        }
      });
      return newMap;
    });

    setPolylineDecoratorsByLatlngs((prev) => {
      const newMap = new Map(prev);
      newMap.delete(latlngsKey);
      return newMap;
    });
  }, []);
  const _created = useCallback(
    (e) => {
      const { layerType, layer } = e;

      if (layerType === "polyline" && layer) {
        drawnItems.addLayer(layer); // Add this line

        const latlngs = layer.getLatLngs();
        const selectedColor = currentColor;

        const manovraIndex = flussi.length + 1;
        const defaultTitle = `Manovra ${manovraIndex}`;

        // Mostra subito tooltip con titolo provvisorio
        layer
          .bindTooltip(defaultTitle, { permanent: true, direction: "top" })
          .openTooltip();

        setFlussiModal({ open: true, currentLang: latlngs, isNew: true });

        layer.setStyle({
          color: selectedColor,
          opacity: 1.0,
          weight: 6.5,
        });
        if (polylineDecorators.has(layer)) {
          const existingDecorator = polylineDecorators.get(layer);
          mapRef.current.removeLayer(existingDecorator);
        }
        const polylineDecorator = L.polylineDecorator(layer, {
          patterns: [
            {
              offset: "100%",
              repeat: 0,
              symbol: L.Symbol.arrowHead({
                pixelSize: 15,
                polygon: true,
                pathOptions: {
                  stroke: true,
                  color: selectedColor,
                  weight: 4.5,
                  fill: true,
                  fillColor: selectedColor,
                  fillOpacity: 1,
                },
              }),
            },
          ],
        }).addTo(mapRef.current);
        setPolylineDecorators((prev) =>
          new Map(prev).set(layer, polylineDecorator)
        );
        const openModal = (e) => {
          if (!deleteOnRef.current)
            setFlussiModal({ open: true, currentLang: latlngs, isNew: true });
          else deleteSingleLine(layer);
        };

        layer.on("click", openModal);
        polylineDecorator.eachLayer((arrowLayer) => {
          arrowLayer.on("click", openModal);
        });

        const defaultName = `Manovra ${manovraIndex}`;
        addFlusso(latlngs, defaultName, selectedColor);
      }
    },
    [flussi, polylineDecorators, deleteOnRef]
  );
  const detectElementPlacement = (event) => {
    const { layer, layerType } = event;
    let elementData = { type: layerType, coordinates: [] };

    if (layerType === "marker") {
      elementData.coordinates = [layer.getLatLng()];
    } else if (layerType === "polyline") {
      elementData.coordinates = layer.getLatLngs();
    } else if (layerType === "rectangle") {
      elementData.coordinates = layer.getBounds();
    } else if (layerType === "circle") {
      elementData.coordinates = {
        center: layer.getLatLng(),
        radius: layer.getRadius(),
      };
    }
    setDrawnItemsData((prevData) => [...prevData, elementData]);


    drawnItems.addLayer(layer);
  };
  const handleOk = (data) => {
    const index = flussi.findIndex(
      (f) =>
        JSON.stringify(f.latlngs) === JSON.stringify(flussiModal.currentLang)
    );

    const defaultTitle = `Manovra ${index + 1}`;
    const newTitle = data.title?.trim() === "" ? defaultTitle : data.title;

    setFlussi(
      flussi?.map((f, i) => {
        if (i === index) {
          return { ...f, data: { ...data, title: newTitle, saved: true } };
        }
        return f;
      })
    );

    updatePolylineColor(flussiModal.currentLang, data.color);
    mapRef.current.eachLayer((layer) => {
      if (
        layer instanceof L.Polyline &&
        JSON.stringify(layer.getLatLngs()) ===
          JSON.stringify(flussiModal.currentLang)
      ) {
        layer
          .bindTooltip(newTitle, { permanent: true, direction: "top" })
          .openTooltip();
      }
    });

    setFlussiModal({ open: false, currentLang: null });
  };

  const DeleteFromEvent = (e) => {
    const latlngs = e.detail;
    const latlngsKey = JSON.stringify(latlngs);

    deleteSingleLine(latlngsKey);
  };

  const handleToggleVisibility = (e) => {
    const { latlngs, visible } = e.detail;
    const latlngsKey = JSON.stringify(latlngs);

    mapRef.current.eachLayer((layer) => {
      if (
        layer instanceof L.Polyline &&
        JSON.stringify(layer.getLatLngs()) === latlngsKey
      ) {
        layer.setStyle({ opacity: visible ? 1 : 0 });

        const tooltip = layer.getTooltip();
        if (tooltip?.getElement()) {
          tooltip.getElement().style.display = visible ? "block" : "none";
        }

        const decorator1 = polylineDecoratorsByLatlngs.get(latlngsKey);
        const decorator2 = polylineDecorators.get(layer);
        const all = [decorator1, decorator2].filter(Boolean);

        all.forEach((dec) => {
          if (!mapRef.current) return;

          const layers = dec._layers || (dec._group && dec._group._layers);
          if (layers) {
            Object.values(layers).forEach((arrowLayer) => {
              if (visible) {
                if (!mapRef.current.hasLayer(arrowLayer)) {
                  mapRef.current.addLayer(arrowLayer);
                }
              } else {
                if (mapRef.current.hasLayer(arrowLayer)) {
                  mapRef.current.removeLayer(arrowLayer);
                }
              }
            });
          }

          if (!visible && mapRef.current.hasLayer(dec)) {
            mapRef.current.removeLayer(dec);
          }
        });

        if (visible && all.length === 0) {
          const polyline = L.polyline(latlngs);
          const newDecorator = L.polylineDecorator(polyline, {
            patterns: [
              {
                offset: "100%",
                repeat: 0,
                symbol: L.Symbol.arrowHead({
                  pixelSize: 15,
                  polygon: true,
                  pathOptions: {
                    stroke: true,
                    color: layer.options.color || "#000000",
                    weight: 4.5,
                    fill: true,
                    fillColor: layer.options.color || "#000000",
                    fillOpacity: 1,
                  },
                }),
              },
            ],
          }).addTo(mapRef.current);

          newDecorator.eachLayer((arrowLayer) => {
            arrowLayer._isArrowHead = true;
            arrowLayer._parentLatlngs = latlngsKey;
          });

          setPolylineDecoratorsByLatlngs((prev) => {
            const updated = new Map(prev);
            updated.set(latlngsKey, newDecorator);
            return updated;
          });
          setPolylineDecorators((prev) => {
            const updated = new Map(prev);
            updated.set(layer, newDecorator);
            return updated;
          });
        }

        layer.options._visible = visible;
      }
    });

    mapRef.current.eachLayer((layer) => {
      if (layer._isArrowHead && layer._parentLatlngs === latlngsKey) {
        if (visible) {
          if (!mapRef.current.hasLayer(layer)) {
            mapRef.current.addLayer(layer);
          }
        } else {
          if (mapRef.current.hasLayer(layer)) {
            mapRef.current.removeLayer(layer);
          }
        }
      }
    });
  };

  const updatePolylineColor = (latlngs, color) => {
    mapRef.current.eachLayer((layer) => {
      if (
        layer instanceof L.Polyline &&
        JSON.stringify(layer.getLatLngs()) === JSON.stringify(latlngs)
      ) {
        layer.setStyle({ color });

        const existingDecorator = polylineDecorators.get(layer);
        if (existingDecorator) {
          mapRef.current.removeLayer(existingDecorator);
          polylineDecorators.delete(layer);
        }

        const polylineDecorator = L.polylineDecorator(layer, {
          patterns: [
            {
              offset: "100%",
              repeat: 0,
              symbol: L.Symbol.arrowHead({
                pixelSize: 15,
                polygon: true,
                pathOptions: {
                  stroke: true,
                  color: color,
                  weight: 4.5,
                  fill: true,
                  fillColor: color,
                  fillOpacity: 1,
                },
              }),
            },
          ],
        }).addTo(mapRef.current);

        setPolylineDecoratorsByLatlngs((prev) => {
          const updated = new Map(prev);
          updated.set(JSON.stringify(latlngs), polylineDecorator);
          return updated;
        });
      }
    });
  };
  var handleCancel = () => {
    setFlussiModal({ open: false, currentLang: null });
  };

  useEffect(() => {
    //TOOLTIP
    mapRef.current?.eachLayer((layer) => {
      if (layer instanceof L.Polyline && layer.getTooltip()) {
        const tooltipEl = layer.getTooltip().getElement();
        if (!tooltipEl) return;

        const isHovered =
          hoveredFlusso &&
          JSON.stringify(layer.getLatLngs()) ===
            JSON.stringify(hoveredFlusso.latlngs);

        if (isHovered) {
          tooltipEl.classList.add("custom-tooltip-hovered");
          tooltipEl.classList.remove("custom-tooltip-default");
        } else {
          tooltipEl.classList.remove("custom-tooltip-hovered");
          tooltipEl.classList.add("custom-tooltip-default");
        }
      }
    });
  }, [hoveredFlusso]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.updatePolylineColor = (latlngs, color) => {
        mapRef.current.eachLayer((layer) => {
          if (
            layer instanceof L.Polyline &&
            JSON.stringify(layer.getLatLngs()) === JSON.stringify(latlngs)
          ) {
            layer.setStyle({ color });

            const existingDecorator = polylineDecorators.get(layer);
            if (existingDecorator) {
              mapRef.current.removeLayer(existingDecorator);
              polylineDecorators.delete(layer);
            }

            const polylineDecorator = L.polylineDecorator(layer, {
              patterns: [
                {
                  offset: "100%",
                  repeat: 0,
                  symbol: L.Symbol.arrowHead({
                    pixelSize: 15,
                    polygon: true,
                    pathOptions: {
                      stroke: true,
                      color,
                      weight: 4.5,
                      fill: true,
                      fillColor: color,
                      fillOpacity: 1,
                    },
                  }),
                },
              ],
            }).addTo(mapRef.current);

            setPolylineDecorators((prev) => {
              const updated = new Map(prev);
              updated.set(layer, polylineDecorator);
              return updated;
            });
          }
        });
      };
    }
  }, [polylineDecorators]);

  useEffect(() => {
    window.addEventListener("toggle-visibility", handleToggleVisibility);

    return () => {
      window.removeEventListener("toggle-visibility", handleToggleVisibility);
    };
  }, []);
  useEffect(() => {
    const handleDeleteAll = () => {
      if (!mapRef.current) return;

      // Remove all polylines and decorators
      mapRef.current.eachLayer((layer) => {
        if (
          layer instanceof L.Polyline ||
          layer instanceof L.PolylineDecorator ||
          layer._isArrowHead
        ) {
          mapRef.current.removeLayer(layer);
        }
      });

      // Clear all references
      renderedPolylinesRef.current.clear();
      setPolylineDecorators(new Map());
      setPolylineDecoratorsByLatlngs(new Map());
    };

    window.addEventListener("delete-all-manovre", handleDeleteAll);
    return () => {
      window.removeEventListener("delete-all-manovre", handleDeleteAll);
    };
  }, []);
  useEffect(() => {
    window.addEventListener("delete-manovra", DeleteFromEvent);

    return () => {
      window.removeEventListener("delete-manovra", DeleteFromEvent);
    };
  }, [mapRef]);

  useEffect(() => {
    const removeDecorator = (e) => {
      const key = JSON.stringify(e.detail);
      setPolylineDecoratorsByLatlngs((prev) => {
        const updated = new Map(prev);
        const decorator = updated.get(key);
        if (decorator) {
          if (mapRef.current?.hasLayer(decorator)) {
            mapRef.current.removeLayer(decorator);
          }
          if (decorator._layers) {
            Object.values(decorator._layers).forEach((l) => {
              if (mapRef.current.hasLayer(l)) {
                mapRef.current.removeLayer(l);
              }
            });
          }
          updated.delete(key);
        }
        return updated;
      });

      setPolylineDecorators((prev) => {
        const updated = new Map(prev);
        for (const [layer, decorator] of updated.entries()) {
          if (
            layer instanceof L.Polyline &&
            JSON.stringify(layer.getLatLngs()) === key
          ) {
            if (mapRef.current?.hasLayer(decorator)) {
              mapRef.current.removeLayer(decorator);
            }
            if (decorator._layers) {
              Object.values(decorator._layers).forEach((l) => {
                if (mapRef.current.hasLayer(l)) {
                  mapRef.current.removeLayer(l);
                }
              });
            }
            updated.delete(layer);
          }
        }
        return updated;
      });
      mapRef.current.eachLayer((layer) => {
        if (layer._isArrowHead && layer._parentLatlngs === key) {
          mapRef.current.removeLayer(layer);
        }
      });
    };

    window.addEventListener("delete-decorator", removeDecorator);
    return () => {
      window.removeEventListener("delete-decorator", removeDecorator);
    };
  }, []);
  const createFlussi = useCallback(() => {
    if (!mapRef.current || alreadySet.current) return;
    alreadySet.current = true;
    flussi.forEach((f) => {
      const key = JSON.stringify(f.latlngs);
      if (f.data?.saved && !renderedPolylinesRef.current.has(key)) {
        // Create the polyline
        const layer = L.polyline(f.latlngs, {
          color: f.data.color || "#000000",
          opacity: 1.0,
          weight: 6.5,
        }).addTo(mapRef.current);

        // Add to feature group (like _created does)
        drawnItems.addLayer(layer);

        // Mark the polyline
        layer._isMainPolyline = true;
        layer._latlngsKey = key;

        // Add tooltip
        const tooltip = f.data.title || `Manovra ${flussi.indexOf(f) + 1}`;
        layer
          .bindTooltip(tooltip, {
            permanent: true,
            direction: "top",
          })
          .openTooltip();

        // Remove existing decorator if any (like _created does)
        if (polylineDecorators.has(layer)) {
          const existingDecorator = polylineDecorators.get(layer);
          mapRef.current.removeLayer(existingDecorator);
        }

        // Create decorator with arrowhead
        const decorator = L.polylineDecorator(layer, {
          patterns: [
            {
              offset: "100%",
              repeat: 0,
              symbol: L.Symbol.arrowHead({
                pixelSize: 15,
                polygon: true,
                pathOptions: {
                  stroke: true,
                  color: f.data.color || "#000000",
                  weight: 4.5,
                  fill: true,
                  fillColor: f.data.color || "#000000",
                  fillOpacity: 1,
                },
              }),
            },
          ],
        }).addTo(mapRef.current);

        // Mark decorator and arrowheads
        decorator._isDecorator = true;
        decorator._latlngsKey = key;
        decorator.eachLayer((arrowLayer) => {
          arrowLayer._isArrowHead = true;
          arrowLayer._parentLatlngs = key;
        });

        // Click handler (matches _created behavior)
        const openModal = () => {
          if (!deleteOnRef.current) {
            setFlussiModal({
              open: true,
              currentLang: f.latlngs,
              isNew: false,
            });
          } else {
            deleteSingleLine(JSON.stringify(f.latlngs));
          }
        };

        layer.on("click", openModal);
        decorator.eachLayer((arrowLayer) => arrowLayer.on("click", openModal));

        // Update references
        renderedPolylinesRef.current.add(key);
        setPolylineDecorators((prev) => new Map(prev).set(layer, decorator));
        setPolylineDecoratorsByLatlngs((prev) =>
          new Map(prev).set(key, decorator)
        );
      }
    });
  }, [flussi, deleteOnRef.current, deleteSingleLine]);
  useEffect(() => {
    createFlussi();
  }, [flussi]);

  const _deleted = useCallback(
    (e) => {
      const { layers } = e;
      layers.eachLayer((layer) => {
        if (layer instanceof L.Polyline) {
          const latlngsKey = JSON.stringify(layer.getLatLngs());

          // Remove from state
          setFlussi((prev) =>
            prev.filter((f) => JSON.stringify(f.latlngs) !== latlngsKey)
          );

          // Remove conflicts related to this maneuver
          setConflittiSelezionati((prev) =>
            prev.filter(([a, b]) => {
              const flusso = flussi.find(
                (f) => JSON.stringify(f.latlngs) === latlngsKey
              );
              return flusso
                ? !(
                    a.includes(`Manovra ${flusso.id}`) ||
                    b.includes(`Manovra ${flusso.id}`)
                  )
                : true;
            })
          );

          // Remove all related layers from map
          mapRef.current.eachLayer((mapLayer) => {
            // Check for main polyline
            const isTargetPolyline =
              mapLayer instanceof L.Polyline &&
              JSON.stringify(mapLayer.getLatLngs()) === latlngsKey;

            // Check for decorators
            const isTargetDecorator =
              mapLayer instanceof L.PolylineDecorator &&
              (mapLayer._latlngsKey === latlngsKey ||
                (mapLayer._layers &&
                  Object.values(mapLayer._layers).some(
                    (l) => l._parentLatlngs === latlngsKey
                  )));

            // Check for arrowheads
            const isTargetArrowHead =
              mapLayer._isArrowHead && mapLayer._parentLatlngs === latlngsKey;

            if (isTargetPolyline || isTargetDecorator || isTargetArrowHead) {
              mapRef.current.removeLayer(mapLayer);
            }
          });

          // Update references
          renderedPolylinesRef.current.delete(latlngsKey);
          setPolylineDecorators((prev) => {
            const newMap = new Map(prev);
            newMap.forEach((decorator, polyline) => {
              if (
                polyline instanceof L.Polyline &&
                JSON.stringify(polyline.getLatLngs()) === latlngsKey
              ) {
                newMap.delete(polyline);
              }
            });
            return newMap;
          });

          setPolylineDecoratorsByLatlngs((prev) => {
            const newMap = new Map(prev);
            newMap.delete(latlngsKey);
            return newMap;
          });
        }
      });
    },
    [flussi, setConflittiSelezionati]
  );

  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }}>
      {alertVisible && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}>
          <Alert
            message={alertMessage}
            type={alertType}
            showIcon
            closable
            onClose={() => setAlertVisible(false)}
          />
        </div>
      )}

      <LineModal
        isModalOpen={flussiModal.open}
        handleOk={handleOk}
        currentLang={flussiModal.currentLang}
        handleCancel={handleCancel}
        isNew={flussiModal.isNew}
        flussi={flussi}
        addFlusso={addFlusso}
      />
      <MapContainer
        center={center}
        zoom={ZOOM_LEVEL}
        ref={mapRef}
        style={{ height: "500px", width: "100%" }}
        onClick={handleMapClick}
      >
        <LocateButton />
        <FeatureGroup ref={featureGroupRef} key={featureGroupKey}>
          <EditControl
            position="topright"
            onCreated={_created}
            onDeleted={_deleted}
            onDeleteStart={() => {
              deleteOnRef.current = true;
              showAlert(
                "Deletion mode activated - click on lines to delete",
                "warning"
              );
            }}
            onDeleteStop={() => {
              deleteOnRef.current = false;
              showAlert("Deletion mode deactivated", "info");
            }}
            draw={{
              circle: { icon: pedestrianIcon },
              marker: { icon: trafficIcon },
              polyline: true,
              rectangle: false,
            }}
            edit={{
              featureGroup: drawnItems, // Make sure this matches your feature group
              edit: true,
              remove: true,
            }}
          />
        </FeatureGroup>
        <TileLayer
          url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
          maxZoom={21}
        />
      </MapContainer>

      <div
        style={{
          marginTop: "50px",
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "16px",
          minHeight: "120px",
        }}
      >
        <h3 style={{ color: "#A1E340", marginBottom: "10px" }}>Conflitti</h3>
        <MatriceConflitti
          flussi={flussi}
          conflittiSelezionati={conflittiSelezionati}
          setConflittiSelezionati={setConflittiSelezionati}
        />

        {(optimizedData?.optimized_fasi_without_estendi ||
          optimizedData?.optimized_fasi) && (
          <>
            <h3
              style={{
                color: "#A1E340",
                marginBottom: "10px",
                marginTop: "25px",
              }}
            >
              Plots
            </h3>
            <Row style={{ width: "100%" }} gutter={[24, 24]}>
              {optimizedData?.optimized_fasi_without_estendi && (
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <div
                    style={{
                      marginBottom: "10px",
                      fontWeight: "bold",
                      fontSize: "15px",
                    }}
                  >
                    Capacità ottimizzata{" "}
                    <span style={{ color: "#888" }}>
                      (prima dell’estendi fasi)
                    </span>
                  </div>
                  <FasiChart
                    fasi={optimizedData.optimized_fasi_without_estendi}
                    Tc={optimizedData.Tc}
                    tempotuttorosso={optimizedData.tempotuttorosso}
                    tempogiallo={optimizedData.tempogiallo}
                    conflitti={conflittiSelezionati}
                  />
                </Col>
              )}
              {optimizedData?.optimized_fasi && (
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <div
                    style={{
                      marginBottom: "10px",
                      fontWeight: "bold",
                      fontSize: "15px",
                      marginLeft: 60,
                    }}
                  >
                    Capacità ottimizzata{" "}
                    <span style={{ color: "#888" }}>(dopo l’estendi fasi)</span>
                  </div>
                  <FasiChart
                    fasi={optimizedData.optimized_fasi}
                    Tc={optimizedData.Tc}
                    tempotuttorosso={optimizedData.tempotuttorosso}
                    tempogiallo={optimizedData.tempogiallo}
                    conflitti={conflittiSelezionati}
                  />
                </Col>
              )}
            </Row>

            {/* ✅ Show legend only if plot exists */}
            <div style={{ marginTop: "20px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "20px",
                  flexWrap: "wrap",
                  fontSize: 11,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: "indianred",
                      border: "1px solid #ccc",
                    }}
                  ></div>
                  <span>Tempo di inattività (rosso chiaro)</span>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: "green",
                      border: "1px solid #ccc",
                    }}
                  ></div>
                  <span>Verde efficace</span>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: "orange",
                      border: "1px solid #ccc",
                    }}
                  ></div>
                  <span>Tempo giallo</span>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: "darkred",
                      border: "1px solid #ccc",
                    }}
                  ></div>
                  <span>Tempo tutto rosso</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MapComponent;
